import { useEffect, useState } from "react";
import { fetchEarlyAccessStats } from "@/lib/api";

/**
 * Live waitlist counter — polls /api/early-access/stats.
 * Renders 3 stat pills with the current signup counts. Real numbers only.
 *
 * Props:
 *  - refreshMs: polling interval (default 30s)
 *  - tone: "dark" | "light" — swap text colors to sit on cream or navy background
 */
const WaitlistCounter = ({ refreshMs = 30000, tone = "dark" }) => {
  const [stats, setStats] = useState(null);
  const [prev, setPrev] = useState(null);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const s = await fetchEarlyAccessStats();
        if (cancelled) return;
        setStats((cur) => {
          if (cur && s.total > cur.total) {
            setBump(true);
            setTimeout(() => setBump(false), 900);
          }
          setPrev(cur);
          return s;
        });
      } catch {
        /* silent — counter is non-critical */
      }
    };
    load();
    const t = setInterval(load, refreshMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [refreshMs]);

  const isDark = tone === "dark";
  const labelCls = isDark ? "text-navy-200" : "text-navy-500";
  const numberCls = isDark ? "text-white" : "text-navy-800";

  const items = [
    { label: "on the waitlist", value: stats?.total },
    { label: "creators", value: stats?.creators },
    { label: "backers", value: stats?.backers },
  ];

  return (
    <div
      data-testid="waitlist-counter"
      className="grid grid-cols-3 gap-3 sm:gap-6"
    >
      {items.map((it) => (
        <div key={it.label} className="flex flex-col">
          <div className="flex items-baseline gap-2">
            <span
              data-testid={`waitlist-count-${it.label.replace(/\s+/g, "-")}`}
              className={`font-display text-3xl sm:text-4xl font-bold tracking-tight ${numberCls} transition-transform ${bump ? "scale-110" : "scale-100"}`}
            >
              {typeof it.value === "number" ? it.value.toLocaleString() : "—"}
            </span>
            {prev && stats && stats.total > prev.total && it.label === "on the waitlist" && (
              <span className="text-[11px] font-semibold text-saffron-300">+{stats.total - prev.total}</span>
            )}
          </div>
          <span className={`text-xs font-semibold uppercase tracking-[0.16em] mt-1 ${labelCls}`}>{it.label}</span>
        </div>
      ))}
    </div>
  );
};

export default WaitlistCounter;
